// Bid Modal — interactive behavior.
// Phase 1: open/close + offer field comma formatting.
// Phase 2: slider + tier state + preset chips + cap.
// Phase 3: form validation + submit toggle + international phone
//          formatting (libphonenumber-js, lazy-loaded) + accordion.
// Phase 5: real submit (fetch) + loading + success + error states.
(function () {
  'use strict';

  // ---------- API ----------
  var API_BASE = 'https://wa-shopper-intelligence.vercel.app';
  var API_PATHS = {
    listed: '/api/inquiries/offer',
    source: '/api/inquiries/source'
  };
  // Post-submit confirmation pages (live in Shopify admin → Pages).
  var REDIRECT_PATHS = {
    listed: '/pages/offer-received-now-lets-make-it-yours',
    source: '/pages/request-received-now-let-s-make-it-yours'
  };

  // ---------- Tier + slider config ----------
  var TIERS = [
    { min: 0.97, name: 'strong',   status: 'A specialist will be in touch within 24 hours' },
    { min: 0.95, name: 'standard', status: 'Likely to receive a response'         },
    { min: 0.93, name: 'yellow',   status: 'Unlikely to be considered'            },
    { min: 0.00, name: 'red',      status: 'Highly unlikely to be considered'     }
  ];
  var NEUTRAL_TIER = { name: 'neutral', status: '' };
  var SLIDER_MIN  = 0.90;
  var SLIDER_MAX  = 0.99;
  var STEP_PCT    = 0.005;
  var DISPLAY_CAP = 99;
  // Below this ratio: red warning under the percentage line + submit
  // disabled with "Offer is too low" in the missing-fields list. The
  // user can still type/commit any value — we don't snap it up.
  var FLOOR_RATIO = 0.90;

  // ---------- Validation ----------
  // Pragmatic email check: rejects trailing-dot / double-dot / missing-TLD typos.
  // Not full RFC 5322 (intentional) — accepts plus-addressing, subdomains, long TLDs.
  var EMAIL_RE = /^[^\s@.]+(?:\.[^\s@.]+)*@(?:[^\s@.-](?:[^\s@.]*[^\s@.-])?\.)+[a-z]{2,}$/i;
  function isValidEmail(v) {
    var s = String(v).trim();
    if (!s || s.indexOf('..') !== -1) return false;
    return EMAIL_RE.test(s);
  }
  // Permissive phone check: 7+ digits anywhere in the input
  function isValidPhone(v) { return String(v).replace(/\D/g, '').length >= 7; }

  // ---------- libphonenumber lazy loader ----------
  var libpnPromise = null;
  function loadLibphonenumber(url) {
    if (libpnPromise) return libpnPromise;
    if (window.libphonenumber) {
      libpnPromise = Promise.resolve(window.libphonenumber);
      return libpnPromise;
    }
    libpnPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload  = function () { resolve(window.libphonenumber); };
      s.onerror = function (e) { libpnPromise = null; reject(e); };
      document.head.appendChild(s);
    });
    return libpnPromise;
  }

  // ---------- Helpers ----------
  function formatDollars(d) {
    if (!d || d < 1) return '';
    return Number(d).toLocaleString('en-US');
  }
  function parseDollars(s) {
    var d = String(s).replace(/\D/g, '');
    return d ? parseInt(d, 10) : 0;
  }
  function tierFor(ratio, dollars) {
    if (!dollars) return NEUTRAL_TIER;
    for (var i = 0; i < TIERS.length; i++) {
      if (ratio >= TIERS[i].min) return TIERS[i];
    }
    return TIERS[TIERS.length - 1];
  }

  // ---------- Init ----------
  function init() {
    var modal   = document.querySelector('.bid-modal');
    var overlay = document.querySelector('[data-bid-modal-overlay]');
    if (!modal || !overlay) return;

    var libpnUrl = modal.getAttribute('data-libphonenumber-url') || '';
    var closeBtn = overlay.querySelector('[data-bid-modal-close]');
    var inputEarly = modal.querySelector('[data-bid-offer-input]');

    // ---------- Open / close ----------
    // Define these early so window.BidModal + URL preview still work even
    // if later setup bails (e.g. on a product with no price).
    function updateSubmitLabel() {
      var btn = modal.querySelector('[data-bid-submit]');
      if (!btn) return;
      // Source mode = no offer field, so "SUBMIT" reads more naturally
      // than "SUBMIT OFFER" (there's no offer being submitted).
      btn.textContent = (modal.getAttribute('data-bid-mode') === 'source')
        ? 'SUBMIT'
        : 'SUBMIT OFFER';
    }

    var visualViewportRef = null;
    var visualViewportHandler = null;
    function syncOverlayForKeyboard() {
      if (!window.visualViewport || !overlay.classList.contains('is-open')) return;
      var occluded = Math.max(0, Math.round(window.innerHeight - window.visualViewport.height));
      overlay.style.paddingBottom = occluded > 0 ? occluded + 'px' : '';
    }
    function attachVisualViewportHandler() {
      if (!window.visualViewport || visualViewportHandler) return;
      visualViewportRef = window.visualViewport;
      visualViewportHandler = function () {
        syncOverlayForKeyboard();
      };
      visualViewportRef.addEventListener('resize', visualViewportHandler);
      syncOverlayForKeyboard();
    }
    function detachVisualViewportHandler() {
      overlay.style.paddingBottom = '';
      if (!visualViewportRef || !visualViewportHandler) return;
      visualViewportRef.removeEventListener('resize', visualViewportHandler);
      visualViewportHandler = null;
      visualViewportRef = null;
    }

    function open(opts) {
      opts = opts || {};
      if (opts.mode) {
        modal.setAttribute('data-bid-mode', opts.mode);
        // Clear the offer when switching to a non-listed mode so a stale
        // pre-filled value doesn't carry over silently.
        if (opts.mode !== 'listed' && inputEarly) inputEarly.value = '';
      }
      // Reset post-submit state so reopening (e.g. after the user bails
      // during an in-flight request) starts the form fresh. After a
      // successful submit we redirect away, so this matters mostly for
      // the close-mid-request case.
      isSubmitting = false;
      updateSubmitLabel();
      // Hoist the overlay to be a direct child of <body> so it escapes any
      // ancestor stacking contexts on the product page (created by transforms,
      // filters, opacity, etc.). Without this, third-party widgets and certain
      // page badges can render above our overlay even at max z-index.
      if (overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'none';
      attachVisualViewportHandler();
      // Lazy-load phone formatter on first open. Failure is non-fatal.
      if (libpnUrl) loadLibphonenumber(libpnUrl).catch(function () {});
    }
    function close() {
      // Move focus out of the modal before aria-hiding it so screen
      // readers don't see a focused descendant of an aria-hidden element.
      if (overlay.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.documentElement.style.overscrollBehavior = '';
      detachVisualViewportHandler();
    }
    window.BidModal = { open: open, close: close };

    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    modal.addEventListener('focusin', function (e) {
      var target = e.target;
      if (!target || !target.matches || !target.matches('input, textarea, select')) return;
      window.setTimeout(function () {
        if (!overlay.classList.contains('is-open')) return;
        if (document.activeElement !== target) return;
        if (!modal.contains(target) || !target.scrollIntoView) return;
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 50);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    // Preview helpers (any of these auto-open the modal):
    //   ?bid-preview → open in current Liquid-set mode
    //   ?bid-source  → open in source mode (force, even on a priced product)
    // Regex on the raw URL — robust to malformed `?param1=val?bid-preview`.
    try {
      var modeOverride = null;
      if (/[?&]bid-source(?:[=&]|$)/.test(window.location.href)) modeOverride = 'source';
      var shouldPreview = modeOverride !== null ||
        /[?&]bid-preview(?:[=&]|$)/.test(window.location.href);
      if (shouldPreview) open(modeOverride ? { mode: modeOverride } : undefined);
      // Sync the submit label to the initial mode, even when not previewing.
      updateSubmitLabel();
    } catch (e) { /* ignore */ }

    // ---------- Mode + asking price ----------
    // The current mode is read live from data-bid-mode (Liquid sets initial,
    // ?bid-source overrides it for preview, Phase 4 trigger buttons will set
    // it via open({ mode: ... })).
    function currentMode() { return modal.getAttribute('data-bid-mode') || 'listed'; }

    var askingCents   = parseInt(modal.getAttribute('data-bid-asking-cents'), 10);
    var hasAsking     = currentMode() === 'listed' && askingCents && !isNaN(askingCents) && askingCents > 0;
    var askingDollars = hasAsking ? Math.round(askingCents / 100) : 0;
    var maxDollars    = hasAsking ? Math.max(0, askingDollars - 1) : 0;

    // ---------- DOM refs ----------
    var input        = inputEarly; // already grabbed above
    var stepBtns     = modal.querySelectorAll('[data-bid-step]');
    var presetBtns   = modal.querySelectorAll('[data-bid-preset]');
    var fillEl       = modal.querySelector('[data-bid-slider-fill]');
    var pctEl        = modal.querySelector('[data-bid-pct]');
    var statusEl     = modal.querySelector('[data-bid-status]');

    var form         = modal.querySelector('[data-bid-form]');
    var submitBtn    = modal.querySelector('[data-bid-submit]');
    var firstName    = form && form.querySelector('[name="first_name"]');
    var lastName     = form && form.querySelector('[name="last_name"]');
    var emailInput   = form && form.querySelector('[name="email"]');
    var phoneInput   = form && form.querySelector('[name="phone"]');
    var contactPh    = form && form.querySelector('[name="contact_phone"]');
    var contactEm    = form && form.querySelector('[name="contact_email"]');

    var trade        = modal.querySelector('[data-bid-trade]');
    var tradeToggle  = modal.querySelector('[data-bid-trade-toggle]');
    var tradeIcon    = modal.querySelector('[data-bid-trade-icon]');
    var tradeText    = modal.querySelector('[data-bid-trade-textarea]');

    // ---------- Offer state (Phase 2) ----------
    if (hasAsking) {
      // ---------- Listed state: full slider + tier + presets ----------
      // Single clamp: only enforces the upper cap (asking − $1). Below
      // 90% is allowed freely — we surface a red warning and disable
      // submit instead of snapping the value up.
      var clampDollars = function (d) {
        if (d > maxDollars) return maxDollars;
        if (d < 0) return 0;
        return d;
      };
      var renderOffer = function (dollars) {
        var ratio = dollars / askingDollars;
        var tier  = tierFor(ratio, dollars);
        var fillRatio;
        if (ratio <= SLIDER_MIN)      fillRatio = 0;
        else if (ratio >= SLIDER_MAX) fillRatio = 1;
        else                          fillRatio = (ratio - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN);

        modal.setAttribute('data-bid-tier', tier.name);
        modal.setAttribute('data-bid-low', (dollars > 0 && ratio < FLOOR_RATIO) ? 'true' : 'false');
        if (pctEl) {
          if (dollars > 0) {
            var displayPct = Math.min(Math.round(ratio * 100), DISPLAY_CAP);
            pctEl.textContent = displayPct + '% of market valuation';
          } else {
            pctEl.textContent = '';
          }
        }
        if (statusEl) statusEl.textContent = tier.status;
        if (fillEl)   fillEl.style.width   = (fillRatio * 100).toFixed(1) + '%';
      };
      var setOffer = function (dollars) {
        var c = clampDollars(Math.round(dollars));
        if (input) input.value = formatDollars(c);
        hasInteracted = true;
        renderOffer(c);
        checkValidity();
      };

      if (input) {
        var hasInteracted = false;
        var clearOfferValue = function () {
          input.value = '';
          renderOffer(0);
          checkValidity();
          hasInteracted = true;
        };
        var clearPrefilledOfferOnFirstInteraction = function () {
          if (hasInteracted) return;
          if (input.value !== input.defaultValue) return;
          clearOfferValue();
        };

        renderOffer(parseDollars(input.value));

        input.addEventListener('focus', clearPrefilledOfferOnFirstInteraction);
        input.addEventListener('touchstart', clearPrefilledOfferOnFirstInteraction, { passive: true });

        input.addEventListener('input', function () {
          var oldValue  = input.value;
          var oldCursor = input.selectionStart;
          var oldLength = oldValue.length;
          var clamped   = clampDollars(parseDollars(oldValue));
          var formatted = formatDollars(clamped);

          if (formatted !== oldValue) {
            input.value = formatted;
            var newLen = formatted.length;
            var newCur = Math.max(0, Math.min(oldCursor + (newLen - oldLength), newLen));
            try { input.setSelectionRange(newCur, newCur); } catch (e) {}
          }
          renderOffer(clamped);
          checkValidity();
        });

        input.addEventListener('blur', function () {
          setOffer(parseDollars(input.value));
        });

        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') {
            e.preventDefault();
            input.blur();
          }
        });
      }

      Array.prototype.forEach.call(stepBtns, function (btn) {
        btn.addEventListener('click', function () {
          var dir = parseInt(btn.getAttribute('data-bid-step'), 10) || 1;
          var step = Math.round(askingDollars * STEP_PCT);
          setOffer(parseDollars(input.value) + dir * step);
        });
      });
      Array.prototype.forEach.call(presetBtns, function (btn) {
        btn.addEventListener('click', function () {
          var pct = parseFloat(btn.getAttribute('data-bid-preset'));
          if (!isNaN(pct)) setOffer(askingDollars * pct);
        });
      });
    }
    // Source mode has no offer field (CSS hides the offer block), so no
    // per-keystroke handler is needed for the input here.

    // ---------- Form validation + submit toggle (Phase 3) ----------
    var formError  = modal.querySelector('[data-bid-form-error]');
    var emailError = modal.querySelector('[data-bid-email-error]');
    var EMAIL_ERROR_MSG = 'Please enter a valid email address, e.g. name@example.com';

    // Inline email error — driven by the SAME isValidEmail gate (no parallel
    // validation path). Reveal on blur / blocked submit; retract on valid input.
    function showEmailErrorIfInvalid() {
      if (!emailError || !emailInput) return;
      emailError.textContent = isValidEmail(emailInput.value) ? '' : EMAIL_ERROR_MSG;
    }
    function clearEmailErrorIfValid() {
      if (!emailError || !emailInput) return;
      if (isValidEmail(emailInput.value)) emailError.textContent = '';
    }

    function getMissingFields() {
      var includesOffer = currentMode() !== 'source';
      var missing = [];
      if (includesOffer && input) {
        var offerDollars = parseDollars(input.value);
        if (offerDollars <= 0) {
          missing.push('Your Offer');
        } else if (askingDollars > 0 && offerDollars / askingDollars < FLOOR_RATIO) {
          missing.push('Offer is too low');
        }
      }
      if (firstName  && !firstName.value.trim()) missing.push('First Name');
      if (lastName   && !lastName.value.trim())  missing.push('Last Name');
      if (emailInput && !isValidEmail(emailInput.value)) {
        missing.push(emailInput.value.trim() ? 'a valid email' : 'Email');
      }
      if (phoneInput && !isValidPhone(phoneInput.value)) {
        missing.push(phoneInput.value.trim() ? 'a valid phone number' : 'Phone Number');
      }
      if (!((contactPh && contactPh.checked) || (contactEm && contactEm.checked))) {
        missing.push('Preferred Contact Method');
      }
      return missing;
    }

    // Accepts either a missing-fields array (uses "Please complete: …"
    // prefix) or a plain string (used as-is for network/server errors).
    function showFormError(arg) {
      if (!formError) return;
      var msg = '';
      if (typeof arg === 'string') {
        msg = arg;
      } else if (arg && arg.length > 0) {
        msg = 'Please complete: ' + arg.join(', ');
      }
      if (!msg) {
        formError.textContent = '';
        formError.setAttribute('hidden', '');
      } else {
        formError.textContent = msg;
        formError.removeAttribute('hidden');
      }
    }

    function checkValidity() {
      var missing = getMissingFields();
      var isValid = missing.length === 0;
      if (submitBtn) {
        submitBtn.classList.toggle('is-disabled', !isValid);
        submitBtn.setAttribute('aria-disabled', isValid ? 'false' : 'true');
      }
      // Auto-clear the error once the form becomes valid (don't pop messages
      // up while the user is still typing — only on explicit submit attempt).
      if (isValid) showFormError([]);
    }

    function findFirstMissing() {
      var includesOffer = currentMode() !== 'source';
      if (includesOffer && input) {
        var offerDollars = parseDollars(input.value);
        if (offerDollars <= 0) return input;
        if (askingDollars > 0 && offerDollars / askingDollars < FLOOR_RATIO) return input;
      }
      if (firstName  && !firstName.value.trim()) return firstName;
      if (lastName   && !lastName.value.trim())  return lastName;
      if (emailInput && !isValidEmail(emailInput.value)) return emailInput;
      if (phoneInput && !isValidPhone(phoneInput.value)) return phoneInput;
      if (!((contactPh && contactPh.checked) || (contactEm && contactEm.checked))) return contactPh;
      return null;
    }

    // First/last/email get the simple validity listener.
    // Phone has its own combined formatter+validity listener below.
    // Contact-method checkboxes use a mutex handler (defined below).
    [firstName, lastName, emailInput].forEach(function (el) {
      if (!el) return;
      el.addEventListener('input',  checkValidity);
      el.addEventListener('change', checkValidity);
    });

    // Email-only inline error: show on blur if malformed, retract as it validates.
    if (emailInput) {
      emailInput.addEventListener('blur',  showEmailErrorIfInvalid);
      emailInput.addEventListener('input', clearEmailErrorIfValid);
    }

    // Mutex: only one Preferred Contact Method checkbox can be active.
    function makeContactToggle(thisOne, otherOne) {
      return function () {
        if (thisOne.checked) otherOne.checked = false;
        checkValidity();
      };
    }
    if (contactPh && contactEm) {
      contactPh.addEventListener('change', makeContactToggle(contactPh, contactEm));
      contactEm.addEventListener('change', makeContactToggle(contactEm, contactPh));
    }

    // ---------- Submit (Phase 5) ----------
    // Cross-session anonymous ID set by the rest of the storefront analytics.
    // Optional — absent on first-touch users; we just omit the field then.
    function getAnonymousId() {
      try { return localStorage.getItem('wa_aid') || null; }
      catch (e) { return null; }
    }

    // Common fields used by both /source and /offer endpoints. Optional
    // fields are omitted when blank so the server sees a clean object.
    function buildBasePayload() {
      var timelineEl = form && form.querySelector('[name="timeline"]');
      var p = {
        first_name: firstName ? firstName.value.trim() : '',
        email:      emailInput ? emailInput.value.trim() : '',
        product_title: modal.getAttribute('data-bid-product-title') || ''
      };
      var ln = lastName  ? lastName.value.trim()  : '';
      var ph = phoneInput ? phoneInput.value.trim() : '';
      var nt = tradeText  ? tradeText.value.trim()  : '';
      var pid  = modal.getAttribute('data-bid-product-id');
      var phn  = modal.getAttribute('data-bid-product-handle');
      var purl = modal.getAttribute('data-bid-product-url');
      var aid  = getAnonymousId();
      var pc   = (contactPh && contactPh.checked) ? 'phone'
               : (contactEm && contactEm.checked) ? 'email'
               : null;
      if (ln)  p.last_name = ln;
      if (ph)  p.phone = ph;
      if (pc)  p.preferred_contact = pc;
      if (pid)  p.product_id = pid;
      if (phn)  p.product_handle = phn;
      if (purl) p.product_url = purl;
      if (timelineEl && timelineEl.value) p.timeframe = timelineEl.value;
      if (nt)  p.notes = nt;
      if (aid) p.anonymous_id = aid;
      return p;
    }

    // Offer payload = base + asking_price + offer_price. Both key names
    // verified end-to-end 2026-05-04 (server 201 + Marc confirmed receipt).
    function buildOfferPayload() {
      var p = buildBasePayload();
      if (askingDollars > 0) p.asking_price = askingDollars;
      if (input) {
        var d = parseDollars(input.value);
        if (d > 0) p.offer_price = d;
      }
      // offer_inquiries.trade_in_note vs source_requests.notes — rename only on offer-mode.
      if (p.notes) { p.trade_in_note = p.notes; delete p.notes; }
      return p;
    }

    function submitForm() {
      var mode = currentMode();
      var path = API_PATHS[mode] || API_PATHS.listed;
      var body = mode === 'source' ? buildBasePayload() : buildOfferPayload();
      return fetch(API_BASE + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (res.ok) return res.json();
        // Try to extract a server-supplied message; fall back to status.
        return res.json().then(
          function (j) {
            var m = (j && (j.error || j.message)) || ('Server returned ' + res.status);
            throw new Error(m);
          },
          function () { throw new Error('Server returned ' + res.status); }
        );
      });
    }

    function setSubmitLoading(loading) {
      if (!submitBtn) return;
      if (loading) {
        submitBtn.classList.add('is-loading');
        submitBtn.classList.remove('is-disabled');
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.textContent = 'SUBMITTING…';
      } else {
        submitBtn.classList.remove('is-loading');
        updateSubmitLabel();
        // Re-evaluate validity to restore disabled-styling correctly.
        checkValidity();
      }
    }

    // Reset the modal to its pristine state. Called before the success
    // redirect so the browser's back-forward cache stores a clean modal
    // (closed, no stale values) — otherwise hitting back from the
    // confirmation page restores the post-submit modal mid-flight.
    function resetModalToFresh() {
      // form.reset() handles name/email/phone/checkboxes/timeline/notes.
      if (form) form.reset();
      // Offer input lives outside the form — reset to its Liquid default
      // and re-render so the slider/tier/percentage all snap back too.
      if (input) {
        input.value = input.defaultValue || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      // Trade-in accordion is JS-state, not form-state.
      tradeOpen = false;
      applyTradeUI();
      showFormError('');
      if (emailError) emailError.textContent = '';
      setSubmitLoading(false);
    }

    // Submit handler. Single in-flight guard prevents double-submits if
    // the user clicks fast or the network is slow.
    var isSubmitting = false;
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (isSubmitting) return;
        var missing = getMissingFields();
        if (missing.length > 0) {
          showFormError(missing);
          showEmailErrorIfInvalid();
          var first = findFirstMissing();
          if (first) first.focus();
          return;
        }
        isSubmitting = true;
        showFormError('');
        var submitMode = currentMode();
        setSubmitLoading(true);
        submitForm()
          .then(function () {
            window.dataLayer = window.dataLayer || [];
            var redirectPath = REDIRECT_PATHS[submitMode] || REDIRECT_PATHS.listed;
            // Carry the product handle so the confirmation page can fetch and
            // render the specific watch the user just submitted on. Read it
            // synchronously (before the modal resets) so the deferred GTM
            // eventCallback below navigates to the correct URL.
            var redirectHandle = modal && modal.getAttribute('data-bid-product-handle');
            if (redirectHandle) {
              redirectPath += '?handle=' + encodeURIComponent(redirectHandle);
            }
            window.dataLayer.push({
              event: 'request_quote',
              formId: submitMode === 'source' ? 1623 : 1717,
              eventCallback: function () { window.location.href = redirectPath; },
              eventTimeout: 300
            });
            close();
            isSubmitting = false;
            resetModalToFresh();
          })
          .catch(function (err) {
            isSubmitting = false;
            setSubmitLoading(false);
            showFormError('Something went wrong. ' + (err && err.message ? err.message : 'Please try again.'));
          });
      });
    }

    // ---------- Phone formatting (Phase 3) ----------
    // Default-country = 'US' so US users can type 10 digits without +1.
    // Anyone starting with + gets formatted per their detected country.
    function formatPhone() {
      if (!phoneInput || !window.libphonenumber) return;
      var raw = phoneInput.value;
      try {
        var asyt = new window.libphonenumber.AsYouType('US');
        var formatted = asyt.input(raw);
        if (formatted && formatted !== raw) {
          var oldCur = phoneInput.selectionStart;
          var oldLen = raw.length;
          phoneInput.value = formatted;
          var newLen = formatted.length;
          var newCur = Math.max(0, Math.min(oldCur + (newLen - oldLen), newLen));
          try { phoneInput.setSelectionRange(newCur, newCur); } catch (e) {}
        }
      } catch (e) { /* leave value as-is */ }
    }
    if (phoneInput) {
      phoneInput.addEventListener('input', function () {
        formatPhone();
        checkValidity();
      });
    }

    // ---------- Trade-in accordion (Phase 3) ----------
    var tradeOpen = false;
    function applyTradeUI() {
      if (!tradeText || !tradeIcon) return;
      var hasText = tradeText.value.trim().length > 0;
      if (tradeOpen) {
        tradeText.removeAttribute('hidden');
        if (hasText) {
          // Lock-open: hide the toggle so user can't collapse with content
          tradeIcon.style.display = 'none';
        } else {
          tradeIcon.style.display = '';
          tradeIcon.textContent = '−';
        }
      } else {
        tradeText.setAttribute('hidden', '');
        tradeIcon.style.display = '';
        tradeIcon.textContent = '+';
      }
    }
    if (tradeToggle) {
      tradeToggle.addEventListener('click', function () {
        var hasText = tradeText && tradeText.value.trim().length > 0;
        // Refuse to collapse if the user has typed something
        if (tradeOpen && hasText) return;
        tradeOpen = !tradeOpen;
        applyTradeUI();
        if (tradeOpen && tradeText) {
          tradeText.focus();
          // Delay scrollIntoView so the mobile keyboard has time to appear
          // before we scroll — otherwise iOS scrolls, then the keyboard
          // pops up and re-occludes the textarea. `block: 'nearest'` does
          // the minimum scroll required (vs 'center' which over-shoots
          // because it doesn't account for the keyboard area).
          setTimeout(function () {
            tradeText.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 300);
        }
      });
    }
    if (tradeText) {
      tradeText.addEventListener('input', applyTradeUI);
    }

    // Initial pass
    checkValidity();
    applyTradeUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
